import React from "react";
import { Spin } from "antd";
// @ts-ignore - useTranslation with namespace parameter
import { useTranslation } from "react-i18next";

/**
 * Suspense fallback for the lazy ProcedureStory chunk — a full-viewport
 * branded overlay so tapping ⓘ produces an immediate, deliberate-looking
 * surface instead of a blank frame while the chunk downloads.
 *
 * Styles are INLINE on purpose: this shell is imported EAGERLY by both
 * select-procedure steps, so importing procedure-story.css here would drag the
 * story's stylesheet into the funnel's eager CSS bundle and defeat the split
 * (mirrors BookingPaymentLoading, and see the LoadingShell warning in
 * client/scripts/check-funnel-bundle.js).
 */
const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 1200,
  background: "rgba(17, 24, 39, 0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};

const closeButtonStyle: React.CSSProperties = {
  border: "none",
  borderRadius: 9999,
  padding: "10px 22px",
  background: "#111827",
  color: "#ffffff",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};

/**
 * `onClose` is REQUIRED (2026-09-13): this overlay covers the whole viewport
 * while the chunk downloads, and on a slow or failing connection that download
 * can take a long time. Without an exit the patient is trapped on a spinner
 * with no way back to the procedure list. Same Close control as
 * ProcedureStoryUnavailable below; the parent owns the open state either way.
 */
const ProcedureStoryLoading: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  // @ts-ignore - useTranslation with namespace parameter
  const { t } = useTranslation("quote");

  return (
    <div style={overlayStyle} aria-busy="true">
      <div
        style={{
          background: "#ffffff",
          borderRadius: 14,
          padding: "28px 36px",
          textAlign: "center",
          boxShadow: "0 8px 24px -4px rgba(17, 24, 39, 0.25)",
        }}
      >
        <Spin />
        <button
          type="button"
          onClick={onClose}
          style={{ ...closeButtonStyle, display: "block", margin: "20px auto 0" }}
        >
          {t("procedures.storyClose", { defaultValue: "Close" })}
        </button>
      </div>
    </div>
  );
};

export default ProcedureStoryLoading;

/**
 * ErrorBoundary fallback for the same chunk. A stale-deploy chunk 404 is
 * already routed to a reload by ErrorBoundary itself; this covers the rest so
 * a failed ⓘ tap never leaves the patient staring at an unresponsive screen
 * with no way back to the procedure list.
 */
export const ProcedureStoryUnavailable: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  // @ts-ignore - useTranslation with namespace parameter
  const { t } = useTranslation("quote");

  return (
    <div style={overlayStyle} role="alertdialog">
      <div
        style={{
          background: "#ffffff",
          borderRadius: 14,
          padding: "24px 28px",
          maxWidth: 360,
          textAlign: "center",
          fontFamily: "system-ui, sans-serif",
          boxShadow: "0 8px 24px -4px rgba(17, 24, 39, 0.25)",
        }}
      >
        <div style={{ fontSize: 15, color: "#262626", marginBottom: 16 }}>
          {t("procedures.storyUnavailable", {
            defaultValue: "We couldn't load the details for this procedure.",
          })}
        </div>
        <button type="button" onClick={onClose} style={closeButtonStyle}>
          {t("procedures.storyClose", { defaultValue: "Close" })}
        </button>
      </div>
    </div>
  );
};
